import { getRootDeputyFrame } from '@webapp-next/global-libs/deputy-embed';
import { Country, CountryState, Permissions } from '@/types/store';
import { HelperEmptyObject, PhpStringBoolean } from '@/types/index';
import {
  ISODateString,
  ISOString,
  ISOStringOffset,
  TimeString24h,
} from '@/types/date-types';
import {
  CustomFieldTypeValues,
  CustomFieldValidation,
  EmploymentTerm,
  Role,
  Workplace,
} from '@/modules/people/types/people-setup';
import {
  View as BackboneView,
  Model as BackboneModel,
  Collection as BackboneCollection,
  Router as BackboneRouter,
} from 'backbone';
import Employee from '@/modules/people/types/employee';
import { Shift } from '@/modules/schedule/types/schedule';
import { SUBSCRIPTION } from '@/lib/webapp-sdk/subscription';

declare global {
  interface Window {
    DP: WebappClassicWindow['DP'];
    DP_ENVIRONMENT: WebappClassicWindow['DP_ENVIRONMENT'];
    DP_SERVER_INSTANCE: WebappClassicWindow['DP_SERVER_INSTANCE'];
  }
}

export type WebappIframeApp = {
  $el: HTMLElement;
  $holder: HTMLElement;
  $iFrame: HTMLIFrameElement;
  appKey: string;
  appName: string;
  appURL: string;
  blnFirstFocusDone: boolean;
  blnHeightTrack: boolean;
  blnKeepFullSpace: boolean;
  cid: string;
  closeApp: () => void;
  doCloseCleanUp: () => void;
  doModalApp: () => void;
  el: HTMLElement;
  height: number;
  hide: () => void;
  launch: () => void;
  render: () => void;
  setHolder: (el: HTMLElement) => void;
  setTitle: (title: string) => void;
  timerHeightTracking: number;
  win: Window;
  winName: string;
};

export type Webapp$dt = {
  EnableRemoteSupport: string;
  HR: {
    onboarding: { canWalkme: () => boolean };
  };
  Modal: BackboneView;
  ResellerContact: {
    Name: string;
    Email: string;
    Phone: string;
  };
  areaWithGeoEnabled: (model: BackboneModel) => false | number;
  arrAppList: Record<string, WebappIframeApp>;
  blnEmployeeSchedulingViewEnabled: boolean;
  blnFfRemoveTeamPanels: boolean;
  blnLoggingOut: boolean;
  breadcrumb: {
    changeHandler: () => void;
    elem: Element;
    init: () => void;
    pop: () => void;
    push: (label: string, url: string) => void;
    reset: () => void;
    stack: [];
  };
  canManageRoster: () => boolean;
  cid: string;
  clcCountry: BackboneCollection<BackboneModel<Country>>;
  clcDef: BackboneCollection<BackboneModel>;
  clcEmployee: BackboneCollection<BackboneModel<Employee>>;
  clcState: BackboneCollection<BackboneModel<CountryState>>;
  clearAlertModal: () => void;
  dashboard: {
    addHelperLink: () => void;
    appendWidget: (strCategory: string, vwWidget: BackboneView) => void;
    blnShowSummaryBoard: boolean;
    changeWidgetClass: (mixResult: string, vwWidget: BackboneView) => void;
    checkColumnEmpty: (strCategory: string) => boolean;
    data: Record<string, unknown>;
    ignorePlaceholderLogic: Record<string, boolean>;
    init: () => void;
    initPlaceHolder: (xhrReqArray: unknown[]) => void;
    initialized: boolean;
    intDashBoardWalkMeId: number;
    renderAllInvoked: boolean;
    initHelper: () => true;
    setupBackboneView: () => void;
    strApprovePHClass: string;
    strAttentionPHClass: string;
    strHappenPHrClass: string;
    strUrlMyWidgetInfo: string;
    strUrlMyWidgetRun: string;
    timeOutHandler: () => void;
    vwDefCard: BackboneView;
    vwMaster: BackboneView;
    xhrReqArray: Record<string, unknown[]>;
  };
  data: Record<string, unknown>;
  deFocusGlobalTabs: () => void;
  editEmployee: (intEmployeeId: Employee['Id']) => void;
  endJoyrideForHelp: () => void;
  featureFlags: {
    allFlags: () => Record<string, unknown>;
    check: () => boolean;
    checkPromise: () => Promise<boolean>;
    waitUntilReady: () => Promise<boolean>;
  };
  freemiumTemplate: string;
  getAppByName: (strAppName: string) => void;
  getConfigForWorkplace: (
    intWorkplace: Workplace['Id'],
    strKey: string,
    retDefault: unknown
  ) => unknown;
  getInstallStatus: (ret: Webapp$dt['objMyInstallInfo']) => void;
  getMobileAppSSOLink: (fnCallback: () => unknown) => unknown;
  getShiftDescription: (shift: Shift, intDeptLimit: number) => string;
  handleBillingResponse: (mixedBilling: Record<string, unknown>) => void;
  hasUserPrivilege: (strPrivilege: string) => boolean;
  init: () => void;
  initBackboneModels: () => void;
  intAppSpaceHeight: number;
  intAppSpaceWidth: number;
  intCurrScrolledPos: number;
  intHeartBeatCheck: number;
  isMyDepartment: (deptId: Department['Id']) => boolean;
  isMyDepartmentV2: (ids: Department['Id'][]) => boolean;
  loadJoyrideForHelp: (strAddtionalHelpText: string) => void;
  locationwizard: {
    clcArea: BackboneCollection<BackboneModel<Department>>;
    clcEmployee: BackboneCollection<BackboneModel<Employee>>;
    init: () => void;
    isPlanPopupBlockedForThirdParty: () => void;
    mdlArea: BackboneModel<Department>;
    mdlEmployee: BackboneModel<Employee>;
    modalHide: () => void;
    modalShown: () => void;
    objDefaultGeo: {
      lat: string;
      lon: string;
      countryCode: string;
      city: string;
    };
    objPost: {
      CarryDemoData: -1;
      GeoInfo: {
        city: string;
        countryCode: string;
        deputyCountryId: string;
        lat: string;
        lon: string;
        timezoneId: string;
      };
      CompanyName: string;
      Plan: string;
      SendInvitationEmail: string;
      arrArea: Department[];
      arrEmployee: Employee[];
    };
    setupBackboneModel: () => void;
    setupBackboneView: () => void;
    shouldShowPlanExpiredtrial: () => boolean;
    showWebAppBlocker: () => void;
    vwDefArea: BackboneView;
    vwDefEmployee: BackboneView;
    vwLocationWizard: BackboneView;
    vwMobilePlanUpgradeModal: BackboneView;
    vwMobilePlanUpgradeModalForMobileBrowser: BackboneView;
    vwModal: BackboneView;
    vwResetTrialModal: BackboneView;
    vwStep1: BackboneView;
    vwStep2: BackboneView;
    vwStep3: BackboneView;
    vwStep4: BackboneView;
    vwStepEmpImport: BackboneView;
    vwStepPayment: BackboneView;
  };
  mapBootstrapEventsToResolve: () => void;
  mdlCountry: BackboneModel<Country>;
  mdlEmployee: BackboneModel<Employee>;
  mdlSearchResultItem: BackboneModel;
  mdlState: BackboneModel<CountryState>;
  me: Webapp$dtMe;
  objConfig: Webapp$dtObjConfig;
  objLoginUser: Webapp$dtObjLoginUser;
  objMyInstallInfo: Webapp$dtObjMyInstallInfo;
};

export type Webapp$dtMe = {
  ANALYTICS: {
    DESKTOP_SIDE_TASK_ITEM_COMPLETE: 'SBTC';
    PROFILE_AVAILABILITY_CREATE1: 'PRAD';
    PROFILE_AVAILABILITY_CREATE2: 'PRAE';
    PROFILE_AVAILABILITY_DELETE: 'PRAF';
    PROFILE_AVAILABILITY_VIEW_ALL: 'PRAC';
    PROFILE_LEAVE_CREATE1: 'PRAH';
    PROFILE_LEAVE_CREATE2: 'PRAI';
    PROFILE_LEAVE_VIEW_ALL: 'PRAG';
    PROFILE_ROSTER_VIEW_ALL: 'PRAA';
    PROFILE_TIMESHEET_CREATE1: 'PRAK';
    PROFILE_TIMESHEET_CREATE2: 'PRAL';
    PROFILE_TIMESHEET_VIEW_ALL: 'PRAB';
    PROFILE_VIEW_PHOTO: 'PRAJ';
  };
  BREAK_DONE_STATE: number;
  BREAK_FINISHED_IGNORE_DURATION_STATE: number;
  BREAK_IN_PROGRESS_STATE: number;
  BREAK_IP_STRING: string;
  BREAK_NOT_START_STATE: number;
  BREAK_SCHEDULED_STATE: number;
  CF_DISPLAY_TIMING: {
    CLOCK_IN: number;
    CLOCK_IN_OUT_UPDATE: number;
    CLOCK_OUT_UPDATE: number;
  };
  EditGlobalProfile: () => true;
  MEAL_BREAK_TYPE: 'M';
  REST_BREAK_TYPE: 'R';
  TAKEN_BREAKS: number[];
  TS_ON_BREAK: string;
  addAvailability: () => void;
  addLeave: () => void;
  blnInitDone: boolean;
  blnInitialised: boolean;
  clcAvailability: BackboneCollection<BackboneModel>;
  clcDef: BackboneCollection<BackboneModel>;
  clcRoster: BackboneCollection<BackboneModel<Shift>>;
  clcTimesheet: BackboneCollection<BackboneModel<any>>;
  defer: (fnCallback: () => unknown) => unknown;
  dfdInitialised: Promise<boolean>;
  dtToday: Date;
  init: () => void;
  mdlAvailability: BackboneModel;
  mdlRoster: BackboneModel<Shift>;
  mdlRosterSwap: BackboneModel;
  mdlTimesheet: BackboneModel<any>;
  objConfig: {
    leaveEndDayFormat: string;
    leaveStartDayFormat: string;
    leavesToShow: number;
    otherWeekDayFormat: string;
    rostersToShow: number;
    shortAusDateFormat: string;
    shortTimeFormat: string;
    strLenForRoster: number;
    tasksToShow: number;
    thisWeekDayFormat: string;
    url: string;
  };
  objLeaveModule: {
    DATE_LONG_FORMAT_PHP: string;
    DATE_SHORT_FORMAT_JS: string;
    DATE_SHORT_FORMAT_PHP: string;
    LEAVE_RULE_MIXED: { VALUE: number; TEXT: string };
    STATUS_DESC: Array<{ TEXT: string; CSS_CLASS: string }>;
    STATUS_ENUM: {
      APPROVED: 1;
      AWAITING: 0;
      CANCELLED: 3;
      DATE_APPROVED: 4;
      DECLINED: 2;
      PAY_APPROVED: 5;
    };
    TIME_SHORT_FORMAT: string;
    WEEK_FIRST_DAY: number;
    afterRefreshOneOffCallback: string;
    blnDebug: boolean;
    blnGotLeaveData: boolean;
    blnGotLeaveRecords: boolean;
    clcAvailability: BackboneCollection<BackboneModel>;
    clcCompany: BackboneCollection<BackboneModel<Workplace>>;
    clcDefaultHour: BackboneCollection<BackboneModel>;
    clcEmploymentTerm: BackboneCollection<BackboneModel<EmploymentTerm>>;
    clcFutureApprovedLeave: BackboneCollection<BackboneModel>;
    clcLeave: BackboneCollection<BackboneModel>;
    clcLeaveAccrual: BackboneCollection<BackboneModel>;
    clcLeaveHistory: BackboneCollection<BackboneModel>;
    clcLeavePayLine: BackboneCollection<BackboneModel>;
    clcPayDetail: BackboneCollection<BackboneModel>;
    clcPublicHoliday: BackboneCollection<BackboneModel>;
    clcRole: BackboneCollection<BackboneModel<Role>>;
    clcStandardHour: BackboneCollection<BackboneModel>;
    clcSupervisor: BackboneCollection<BackboneModel>;
    fnPushData: (
      mixDataMap: Record<string, unknown>,
      blnDoAutoRefresh: boolean
    ) => void;
    fnPushLeave: (mixDataMap: Record<string, unknown>) => void;
    fnPushLeavePayLinesOfLeave: (
      objLeaveFromServer: Record<string, unknown>
    ) => void;
    mdlAvailability: BackboneModel;
    mdlCompany: BackboneModel<Workplace>;
    mdlDefaultHour: BackboneModel;
    mdlEmploymentTerm: BackboneModel<EmploymentTerm>;
    mdlFutureApprovedLeave: BackboneModel;
    mdlLeave: BackboneModel;
    mdlLeaveAccrual: BackboneModel;
    mdlLeaveAvailabilityDayItem: BackboneModel;
    mdlLeaveAvailabilityEmployeeWrapper: BackboneModel;
    mdlLeaveHistory: BackboneModel;
    mdlLeaveOther: BackboneModel;
    mdlLeavePayLine: BackboneModel;
    mdlLeaveRule: BackboneModel;
    mdlPayDetail: BackboneModel;
    mdlPublicHoliday: BackboneModel;
    mdlRole: BackboneModel<Role>;
    mdlStandardHour: BackboneModel;
    mdlSupervisor: BackboneModel;
    objApplicationModule: {
      ANALYTICS: Record<string, string>;
      BREAK_DONE_STATE: number;
      BREAK_FINISHED_IGNORE_DURATION_STATE: number;
      BREAK_IN_PROGRESS_STATE: number;
      BREAK_IP_STRING: string;
      BREAK_NOT_START_STATE: number;
      BREAK_SCHEDULED_STATE: number;
      CF_DISPLAY_TIMING: {
        CLOCK_IN: number;
        CLOCK_IN_OUT_UPDATE: number;
        CLOCK_OUT_UPDATE: number;
      };
      EditGlobalProfile: () => void;
      MEAL_BREAK_TYPE: SlotConfigType.MEAL_BREAK_TYPE;
      REST_BREAK_TYPE: SlotConfigType.REST_BREAK_TYPE;
      TAKEN_BREAKS: number[];
      TS_ON_BREAK: string;
      addAvailability: () => void;
      addLeave: () => void;
      blnInitDone: boolean;
      blnInitialised: boolean;
      clcAvailability: BackboneCollection<BackboneModel>;
      clcDef: BackboneCollection<BackboneModel>;
      clcRoster: BackboneCollection<BackboneModel<Shift>>;
      clcTimesheet: BackboneCollection<BackboneModel<any>>;
      defer: (fnCallback: () => unknown) => unknown;
      dfdInitialised: Promise<boolean>;
      dtToday: Date;
      init: () => void;
      mdlAvailability: BackboneModel;
      mdlRoster: BackboneModel<Shift>;
      mdlRosterSwap: BackboneModel;
      mdlTimesheet: BackboneModel<any>;
      objConfig: {
        leaveEndDayFormat: string;
        leaveStartDayFormat: string;
        leavesToShow: number;
        otherWeekDayFormat: string;
        rostersToShow: number;
        shortAusDateFormat: string;
        shortTimeFormat: string;
        strLenForRoster: number;
        tasksToShow: number;
        thisWeekDayFormat: string;
        url: string;
      };
      objLeaveModule: Webapp$dt['me']['objLeaveModule'];
      objRosters: Record<Shift['Id'], Shift>;
      objServerDate: Date;
      rtrME: BackboneRouter;
      setupAvailability: () => void;
      setupCollections: () => void;
      setupLeaveModule: () => void;
      setupLeaveViews: () => void;
      setupPrimarySidePanel: () => void;
      setupProfile: () => void;
      setupProfilePhoto: () => void;
      setupRosterViews: () => void;
      setupRosters: () => void;
      setupRouter: () => void;
      setupSecondarySidePanel: () => void;
      setupSideBarTasksView: () => void;
      setupSidePanel: () => void;
      setupTimesheets: () => void;
      transact: (
        url: string,
        type: string,
        objParam: Record<string, unknown>,
        fnSuccess: () => unknown,
        fnFailed: () => unknown
      ) => Promise<unknown>;
      unavailabilityListPrimary: [];
      unavailabilityListSecondary: [];
      viewLeave: BackboneView;
      viewProfile: BackboneView;
      viewProfilePhoto: BackboneView;
      viewRosters: BackboneView;
      viewTimesheets: BackboneView;
      vwAvailabilityRepeater: BackboneView;
      vwDefAvailabilityForm: BackboneView;
      vwDefAvailabilityFormVue: BackboneView;
      vwDefAvailabilityListItem: BackboneView;
      vwDefLeave: BackboneView;
      vwDefLeaveAccrualListItem: BackboneView;
      vwDefLeaveForm: BackboneView;
      vwDefRosterListItemInPrimary: BackboneView;
      vwDefRosterListItemInSecondary: BackboneView;
      vwDefRosterListItemInSecondaryForLeave: BackboneView;
      vwDefShift: BackboneView;
      vwDefShiftSlidingPanel: BackboneView;
      vwDefTaskRow: BackboneView;
      vwDefTimesheet: BackboneView;
      vwDefTimesheetListItemInPrimary: BackboneView;
      vwDefTimesheetListItemInSecondary: BackboneView;
      vwLeaveRepeater: BackboneView;
      vwModalUnscheduled: BackboneView;
      vwPrimarySidePanel: BackboneView;
      vwProfile: BackboneView;
      vwProfilePhoto: BackboneView;
      vwSecondarySidePanel: BackboneView;
      vwShift: BackboneView;
      vwShiftRepeater: BackboneView;
      vwSidePanel: BackboneView;
      vwTask: BackboneView;
      vwTimesheetRepeater: BackboneView;
    };
    refresh: (fnCallback: () => unknown) => unknown;
    reset: () => void;
    vwDefLeave: BackboneView;
    vwDefLeaveAccrualListItem: BackboneView;
    vwDefLeaveAvailabilityDayItem: BackboneView;
    vwDefLeaveAvailabilityEmployeeWrapper: BackboneView;
    vwDefLeaveBalanceAccrualListItem: BackboneView;
    vwDefLeaveHistoryListItem: BackboneView;
    vwDefLeaveListItem: BackboneView;
    vwDefLeaveOther: BackboneView;
    vwDefLeavePayLine: BackboneView;
  };
};

export type WorkplaceConfig = Record<
  Workplace['Id'],
  {
    WEEK_START: number;
    ACTIVE_HOURS_START: TimeString24h;
    AUTO_SUGGEST_BREAK: PhpStringBoolean;
    CAN_BUMP_SHIFT_VIA_DESK: PhpStringBoolean;
    CAN_CLOCKIN_SHIFT_EARLIER: PhpStringBoolean;
    CAN_CLOCKIN_SHIFT_EARLIER_MINS: number;
    CAN_END_BREAK_EARLIER: PhpStringBoolean;
    CAN_MOBILE_BUMP_SHIFT: PhpStringBoolean;
    CAN_MODIFY_TIMESHEET_ON_END: PhpStringBoolean;
    CAN_SUBMIT_SHIFT_VIA_DESK: PhpStringBoolean;
    DEFAULT_MEALBREAK_DURATION: number;
    GEOFENCE_RADIUS: string;
    MEALBREAK_IS_PAID: string;
    ROSTER_ALLOW_OFFER_SHIFT: PhpStringBoolean;
    ROSTER_ALLOW_PEER_VIEW: PhpStringBoolean;
    ROSTER_ALLOW_SWAP_SHIFT: PhpStringBoolean;
    ROSTER_DEFAULT_SHIFT_LEN: number;
    ROSTER_SWAP_REQUIRE_APPROVAL: PhpStringBoolean;
    TIMESHEET_CLOSEST_BLOCK: number;
    TIMEZONE: string;
    SYSTEM_LOCALE: string;
    CAN_REFERRAL: PhpStringBoolean;
    CAN_ACCESS_STAFFX: PhpStringBoolean;
    ALLOW_EMPS_SEE_LEAVE_BALANCE: string;
    ELIGIBLE_FOR_CONNECT: PhpStringBoolean;
    ALLOW_KIOSK_PHOTO_CLOCKIN: PhpStringBoolean;
    AUTO_RECALCULATE_TIMESHEETS: PhpStringBoolean;
    IS_NOTE_ATTACHMENT_PUBLIC: null;
    ALLOW_CALENDAR_SYNC: null;
    SHIFT_DEFAULT_COST: number;
    EMPLOYEE_LABEL: string;
    MONDAY_ACTIVE_HOURS_START: TimeString24h;
    MONDAY_ACTIVE_HOURS_END: TimeString24h;
    TUESDAY_ACTIVE_HOURS_START: TimeString24h;
    TUESDAY_ACTIVE_HOURS_END: TimeString24h;
    WEDNESDAY_ACTIVE_HOURS_START: TimeString24h;
    WEDNESDAY_ACTIVE_HOURS_END: TimeString24h;
    THURSDAY_ACTIVE_HOURS_START: TimeString24h;
    THURSDAY_ACTIVE_HOURS_END: TimeString24h;
    FRIDAY_ACTIVE_HOURS_START: TimeString24h;
    FRIDAY_ACTIVE_HOURS_END: TimeString24h;
    SATURDAY_ACTIVE_HOURS_START: TimeString24h;
    SATURDAY_ACTIVE_HOURS_END: TimeString24h;
    SUNDAY_ACTIVE_HOURS_START: TimeString24h;
    SUNDAY_ACTIVE_HOURS_END: TimeString24h;
    FLAG_BREAK_DURATION: string;
    TIMESHEET_MATURITY: string;
    TIMESHEET_ROUND_START_TIME: string;
    TIMESHEET_ROUND_END_TIME: string;
    TIMESHEET_ROUND_MEALBREAK: string;
    TIMESHEET_ROUND_START_TIME_RS: string;
    TIMESHEET_ROUND_MEALBREAK_RS: string;
    TIMESHEET_ROUND_END_TIME_RS: string;
    FLAG_START_AND_END_TIMES: string;
    TIMESHEET_AUTO_PAY_APPROVE: string;
  }
>;

export enum SlotConfigType {
  MEAL_BREAK_TYPE = 'M',
  REST_BREAK_TYPE = 'R',
}

export type SlotConfig = {
  SlotValue: SlotConfigType;
  SlotName: string;
  SlotLabel: string;
};

export type SlotsConfig = SlotConfig[];

// export enum BreakType {
//   NO_TYPE = 'NO_TYPE',
//   MEAL_BREAK = 'MEAL_BREAK',
//   REST_BREAK = 'REST_BREAK',
// }
//
// export type Break = {
//   breakType: BreakType;
//   duration: number;
//   startTimeOffset: number | null;
// };

// Area | Department
export type Department = {
  Id: number;
  Company: number;
  OperationalUnitName: string;
  Active: boolean;
  ShowOnRoster: boolean;
  CompanyCode: string;
  CompanyName: string;
  AddressObject: DepartmentAddress[];
  Colour: string;
  ParentOperationalUnit: number;
};

export type DepartmentAddress = {
  Id: number;
  ContactName: null | string;
  UnitNo: null | string;
  StreetNo: null | string;
  SuiteNo: null | string;
  PoBox: null | string;
  Street1: string;
  Street2: null | string;
  City: null | string;
  State: null | string;
  Postcode: null | string;
  Country: number;
  Phone: null | string;
  Notes: null | string;
  Format: null | string;
  Saved: boolean;
  Creator: number;
  Created: string; // '2019-05-23T01:34:47+10:00';
  Modified: string; // '2019-05-23T01:34:47+10:00';
  Print: string;
};

export enum CustomFieldType {
  TEXT = 1,
  NUMBER = 2,
  TEXTAREA = 3,
  BOOLEAN = 4,
  LIST = 5,
  MULTI_LIST = 6,
  FILE = 7,
  BOOLEAN_YES = 8,
  BOOLEAN_NO = 9,
  MULTI = 10, // TODO: verify this type as I wasn't able to locate it
}

export type CustomFieldCollection = {
  Schedule: CustomFields;
  Employee: CustomFields;
  Timesheet: CustomFields;
};

export type CustomFields = CustomField[];
export type CustomField = {
  Default: number | string;
  DeputyField: string;
  Name: string;
  Helptext: string;
  Published: number;
  SortOrder: number;
  Id: number;
  System: string;
  ApiName: string;
  Type: CustomFieldTypeValues;
  DisplayTiming?: string;
  ConditionalRules?: string;
  Action?: string;
  Valuelist: (string | number)[];
  Visible: boolean;
  TriggerScript: string;
  Validation: CustomFieldValidation[];
  Creator: number;
  Created: ISOString;
  Modified: ISOString;
};

export type Webapp$dtObjLoginUser = {
  AllowLogin: boolean;
  ApproveTimesheetCounter: number;
  Created: string;
  Creator: number;
  CustomPronouns: string;
  DisplayName: string;
  EmailAddress: string;
  EmployeeProfile: number;
  EmployeeProfileObject: {
    Active: boolean;
    AllowAppraisal: boolean;
    Company: number;
    Contact: number;
    Created: string;
    Creator: number;
    CustomFieldData: number;
    CustomPronouns: null;
    DateOfBirth: string;
    DisplayName: string;
    EmergencyAddress: null;
    FirstName: string;
    Gender: number;
    HigherDuty: string;
    HistoryId: number;
    Id: number;
    JobAppId: string;
    LastName: string;
    MainAddress: string;
    Modified: string;
    OnboardingId: string;
    OtherName: string;
    Photo: number;
    Position: string;
    PostalAddress: string;
    Pronouns: number;
    Role: number;
    Salutation: string;
    StartDate: string;
    StressProfile: number;
    TerminationDate: string;
    UserId: number;
  };
  Expire: string;
  FirstName: string;
  HasEnabled2FA: boolean;
  Id: number;
  LastName: string;
  Login: string;
  Mobile: string;
  Modified: string;
  Photo: string;
  PhotoObject: string;
  Pronouns: 2;
  PublishRosterCounter: number;
  Status: number;
  URL: string;
  UUID: string;
};

export type PayrollAccess = {
  BusinessEntity: { BusinessNumber: string };
  PayCalendar: { id: number }[];
};

export type Webapp$dtObjConfig = {
  InstanceId: string;
  Me: {
    PayrollAccess: PayrollAccess[];
    Login: string;
    Name: string;
    LastName: string;
    FirstName: string;
    Company: number;
    CompanyObject: {
      Id: number;
      Portfolio: string | null;
      Code: string;
      Active: boolean;
      ParentCompany: number;
      CompanyName: string;
      TradingName: string | null;
      BusinessNumber: string | null;
      CompanyNumber: string | null;
      IsWorkplace: boolean;
      IsPayrollEntity: boolean;
      PayrollExportCode: string | null;
      Address: number;
      Contact: number;
      Creator: number;
      Created: ISOString;
      Modified: ISOString;
    };
    Portfolio: string;
    DeputyVersion: string;
    UserId: number;
    Pronouns: number;
    CustomPronouns: string;
    EmployeeId: number;
    PrimaryEmail: string;
    PrimaryPhone: string | null;
    Permissions: Permissions[];
    JournalCategories: [];
    InProgressTS: string | null;
    UserSince: ISOString;
    UserObjectForAPI: {
      Id: number;
      DisplayName: string;
      Employee: number;
      Photo: string;
    };
    OPS: string[];
    MemosToConfirm: string[];
    CalendarURL: string;
    IsLoggedInViaPortal: boolean;
  };
  MySetup: {
    AvailableKeys: Permissions[];
    Portfolio: string;
    ColorThemeCode: string;
    IsGenderPronounsEnabled: boolean;
    isPreferredNameOptionsEnabled: boolean;
    PortfolioLogoUrl: string;
    DeputyVersion: string;
    SubscriptionType: SUBSCRIPTION;
    Workplace: Workplace[];
    CanAddWorkplace: boolean;
    CanEditWorkplace: boolean;
    CanDeleteWorkplace: boolean;
    CanEditArea: boolean;
    CanAddArea: boolean;
    CanDeleteArea: boolean;
    WorkplaceConfig: WorkplaceConfig;
    Department: Department[];
    Role: Role;
    Permissions: Permissions[];
    IsInitialConfigDone: string;
    SSOEnforce: false;
    SAMLUrl: string;
    PortfolioCreatedDate: string;
    PortfolioCountryCode: string;
    SlotsConfig: SlotsConfig;
    CustomField: CustomFieldCollection;
    // CustomFieldType: Record<keysof CustomFieldCollection, Record<valueof CustomFieldType, CustomFieldType>>;
    EmployeeLabel: string;
    EmployeeLabelPlural: string;
    ShiftLabel: string;
    ShiftLabelPlural: string;
    EnableEmployeeSentiment: false;
    CanSubmitUnavailability: true;
    BusinessLocale: string;
    success: true;
  };
  EmployeeLabel: string;
  DefaultLongDateFormat: string;
  DefaultLongTimeFormat: string;
  DefaultShortDateFormat: string;
  DefaultShortDateFormatJs: string;
  DefaultShortTimeFormat: string;
  DefaultShortTimeFormatJs: string;
  TimesheetShiftPlanChildrenTimeThreshold: string;
  TimesheetShiftPlanParentStartTimeThreshold: string;
  TimesheetShiftPlanParentEndTimeThreshold: string;
  TimesheetBusinessAttestation: boolean;
  GlobalSettingsShowOtherLocations: boolean;
  CurrentLoginUserUuid: string;
  Country: string;
};

export type Webapp$dtObjMyInstallInfo = {
  ThirdPartyUpdateUrl: string;
  blnSubscriptionAtProblemState: boolean;
  blnGamingfyEnabled: boolean;
  blnEnterpriseCustomer: number;
  blnSignupToTrialPaidAcquisitionChannel: boolean;
  dtTrialEndDate: string;
  dtBillingCreated: ISODateString;
  dtBillingCreatedTime: ISOStringOffset;
  intTrialDaysLeft: number;
  SalesSegment: string;
  InstallId: string;
  BillingUrl: string;
  UpgradeUrl: string;
  PortfolioName: string;
  Subscription: number;
  CountryCode: string;
};

export interface Webapp$re {
  vwRosterWrapper: {
    intCurrentViewMode: number;
  };
}

export interface WebappClassicWindow extends Window {
  DP_LOCALE_INFO: string;
  $dt: Webapp$dt;
  $re: Webapp$re;
  DP_ENVIRONMENT: 'test' | 'dev' | 'local' | 'prod';
  DP_SERVER_INSTANCE: 'dev' | 'prod' | 'prodtest';
  DP_SUBSCRIPTION: SUBSCRIPTION;
  DP_SUBSCRIPTION_PAUSED: boolean;
  DP: {
    BootstrapPromise: {
      id?: number;
      get: <T = any>(promiseId: string) => Promise<T>;
      resolve: (promiseId: string, data: unknown) => void;
    };
    PHP_JS: {
      // DP_PHP_JS
      get_html_translation_table: (
        table: string,
        quote_style: string
      ) => false | string;
      htmlentities: (
        string: string,
        quote_style: string | string[] | null,
        charset: string | null,
        double_encode: boolean | null
      ) => void;
      htmlspecialchars: (
        string: string,
        quote_style: string | string[] | null,
        charset: string | null,
        double_encode: boolean | null
      ) => void;
      in_array: (needle: string, haystack: [], argStrict: boolean) => boolean;
      trim: (str: string, charlist: string) => string;
      ltrim: (str: string, charlist: string) => string;
      rtrim: (str: string, charlist: string) => string;
      nl2br: (str: string) => string;
      smartTruncate: (
        strText: string,
        intLimit: number,
        blnAddTitle: boolean
      ) => string;
      sprintf: () => string;
    };
    SUBSCRIPTION: SUBSCRIPTION;
    AGGR_SUM_ACTION: number;
    BREAK_LEVEL_DAY: number;
    BREAK_LEVEL_HOUR: number;
    BREAK_LEVEL_QUARTER_DAY: number;
    MD5: (string: string) => string;
    SHA256: (string: string) => string;
    Validations: {
      validateMobile: (string: string) => boolean;
    };
    adjustBodyForm: () => void;
    ajaxLoadingCustomCoordinates: boolean;
    ajaxLoadingIconMultiplier: number;
    ajaxLoadingMouse: boolean;
    ajaxLoadingMouseHeight: number;
    ajaxLoadingMouseOffsetX: number;
    ajaxLoadingMouseOffsetY: number;
    ajaxLoadingMouseSpin: (spinner: string) => void;
    ajaxLoadingMouseSpinCheck: (spinner: string) => void;
    ajaxLoadingMouseWidth: number;
    ajaxSpinnerIcon: null | string;
    area_color_shared: {
      vwDefPicker: () => void;
    };
    base64Decode: (base64String: string) => string;
    mushodo: {
      blnDebug: boolean;
      flagNochange: '__nochange';
      flagDeleted: '__deleted';
      blnSyncInProgress: boolean;
      intLastCommTimestamp: number;
      _mixedChannelsArray: Array<{
        channel: string;
        singleCall: boolean;
        getParams: () => unknown;
        success: (params: unknown) => unknown;
        error: () => unknown;
      }>;
      _mixedResponseMap: Record<string, HelperEmptyObject>;
      _ns: string;

      askMultiple: () => void; // ƒ askMultiple(mixedQuestionsArray,blnSyncAll,blnBlockGlobalEvents)

      askSingle: MushodoAskSingle<unknown>;
      getRepeaterModificationArray: (
        strRepeaterId: string
      ) => Record<string, unknown>;
      // handleError
      //   :
      //   ƒ handleError(strChannel)
      // handleResponse
      //   :
      //   ƒ handleResponse(objReply)
      post: MushodoPost<unknown>;
      register: (
        strChannel: string,
        fnGetParams: () => Record<string, unknown>,
        fnReturnHandler: () => void,
        fnOnError: () => void,
        blnSingleCall: boolean
      ) => void;
      sanitizePostParam: (
        objParam: Record<string, unknown>
      ) => Record<string, unknown>;
      sync: MushodoSync<unknown>;
      syncAllIndependently: (blnBlockGlobalEvents: boolean) => void;
      unregister: (strRegistrationId: string) => void;
      // unregisterChannel
      //   :
      //   ƒ unregisterChannel(strChannel)
      updateDivById: (
        strDivId: string,
        mixedVariables: string,
        strTemplateId: string
      ) => void;
      // updateRepeater
      //   :
      //   ƒ updateRepeater(strDivId,recordsArray)
    };
  };
}

export type MushodoPost<T> = (
  objParam: HelperEmptyObject,
  blnBlockGlobalEvents: boolean,
  strChannels: string
) => T;

export type MushodoSync<T> = (
  mixedRequestArray: {
    channel: string;
    param: Record<string, unknown>;
  }[],
  blnBlockGlobalEvents: boolean
) => T;

export type MushodoAskSingle<T> = (
  strChannel: string,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  mixedParams: Record<string, any> | string,
  fnReturnHandler: (response: any) => any,
  fnOnError: (response: unknown) => T,
  blnSyncAll: boolean,
  blnBlockGlobalEvents: boolean
) => MushodoPost<T> | MushodoSync<T>;

export function getWebappClassicWindow(): WebappClassicWindow {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return getRootDeputyFrame();
}
